/**
 * i18n provider/hook.
 *
 * - `t(key, params)` interpolates `{param}` placeholders and returns `⟦key⟧` for a missing key
 *   (tests assert no such marker is ever rendered).
 * - Locale is persisted under `nimeet.meeting.locale`; default is `en-US` so the existing recording
 *   runner and screenshots keep working while the user can switch to 中文 and stay switched.
 * - `?lang=zh-CN|en-US` in the URL wins once, then is persisted.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { LOCALES, isLocaleCode, translate, type LocaleCode, type MessageKey } from './messages';

export const LOCALE_STORAGE_KEY = 'nimeet.meeting.locale';

export type TranslateParams = Record<string, string | number | boolean | null | undefined>;
export type Translate = (key: MessageKey, params?: TranslateParams) => string;

export { translate } from './messages';

interface I18nContextValue {
  locale: LocaleCode;
  setLocale: (locale: LocaleCode) => void;
  t: Translate;
  locales: readonly LocaleCode[];
}

const I18nContext = createContext<I18nContextValue | null>(null);

function safeStorageRead(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageWrite(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode / sandboxed iframe: keep the in-memory choice only.
  }
}

/** URL `?lang=` first, then the stored choice, then `en-US`. */
export function initialLocale(search?: string): LocaleCode {
  const query = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  try {
    const fromUrl = new URLSearchParams(query).get('lang');
    if (isLocaleCode(fromUrl)) return fromUrl;
  } catch {
    // ignore malformed query strings
  }
  const stored = safeStorageRead(LOCALE_STORAGE_KEY);
  return isLocaleCode(stored) ? stored : 'en-US';
}

export function I18nProvider({
  children,
  initial,
}: {
  children: ReactNode;
  initial?: LocaleCode;
}): ReactNode {
  const [locale, setLocaleState] = useState<LocaleCode>(() => initial ?? initialLocale());

  useEffect(() => {
    safeStorageWrite(LOCALE_STORAGE_KEY, locale);
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: LocaleCode) => {
    if (!isLocaleCode(next)) return;
    setLocaleState(next);
  }, []);

  const t = useCallback<Translate>((key, params) => translate(locale, key, params), [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t, locales: LOCALES }),
    [locale, setLocale, t],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside <I18nProvider>');
  return context;
}

/** Small language switcher used by the join screen and every room header. */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { locale, setLocale, t } = useI18n();
  return (
    <label className={className ?? 'nm-lang'}>
      <span className="nm-visually-hidden">{t('lang.label')}</span>
      <select
        className="nm-select nm-select--compact"
        aria-label={t('lang.label')}
        data-testid="lang-select"
        value={locale}
        onChange={(event) => {
          if (isLocaleCode(event.target.value)) setLocale(event.target.value);
        }}
      >
        <option value="en-US">{t('lang.en')}</option>
        <option value="zh-CN">{t('lang.zh')}</option>
      </select>
    </label>
  );
}
